/**
 * 문제집 상세 페이지
 * - 문제 목록 탭
 * - 배정 현황 탭
 * - 통계 탭
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Tabs, Button, message, Spin, Table, Tag, Modal } from 'antd';
import { ArrowLeftOutlined, UserAddOutlined, DeleteOutlined, FilePdfOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { workbookService } from '../../services/workbookService';
import { assignmentService } from '../../services/assignmentService';
import { ProblemList } from '../../components/workbooks/ProblemList';
import { AssignmentStats } from '../../components/workbooks/AssignmentStats';
import { AssignWorkbookModal } from '../../components/workbooks/AssignWorkbookModal';
import { AnswerSheetGenerator } from '../../components/pdf';
import type { Workbook, Problem } from '../../types/workbook';
import type { StudentAssignment } from '../../types/assignment';

const { TabPane } = Tabs;

/**
 * WorkbookDetailPage 컴포넌트
 * 문제집 상세 정보 페이지
 */
export const WorkbookDetailPage: React.FC = () => {
  const { workbookName } = useParams<{ workbookName: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [workbook, setWorkbook] = useState<Workbook | null>(null);
  const [assignments, setAssignments] = useState<StudentAssignment[]>([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [unassignModalOpen, setUnassignModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<StudentAssignment | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // PDF 답안지 생성 관련 상태
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [problems, setProblems] = useState<Problem[]>([]);

  /**
   * 문제집 정보 조회
   */
  const fetchWorkbook = async () => {
    if (!workbookName) return;

    setLoading(true);
    try {
      const data = await workbookService.getWorkbook(decodeURIComponent(workbookName));
      setWorkbook(data);
    } catch (error: any) {
      message.error('문제집 정보 조회 실패');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 배정 현황 조회
   */
  const fetchAssignments = async () => {
    if (!workbookName) return;

    try {
      const data = await assignmentService.getWorkbookAssignments(
        decodeURIComponent(workbookName)
      );
      setAssignments(data);
    } catch (error) {
      console.error('배정 현황 조회 실패:', error);
    }
  };

  /**
   * 문제 목록 조회 (PDF 생성용)
   */
  const fetchProblems = async () => {
    if (!workbookName) return;

    try {
      const data = await workbookService.getProblems(decodeURIComponent(workbookName));
      setProblems(data);
    } catch (error) {
      console.error('문제 목록 조회 실패:', error);
    }
  };

  /**
   * 초기 로드
   */
  useEffect(() => {
    fetchWorkbook();
    fetchAssignments();
    fetchProblems();
  }, [workbookName, refreshKey]);

  /**
   * 뒤로 가기
   */
  const handleBack = () => {
    navigate('/workbooks');
  };

  /**
   * 배정 모달 열기
   */
  const handleOpenAssignModal = () => {
    setAssignModalOpen(true);
  };

  /**
   * 배정 성공 처리
   */
  const handleAssignSuccess = () => {
    setRefreshKey((prev) => prev + 1); // 새로고침
  };

  /**
   * 배정 해제 모달 열기
   */
  const handleUnassign = (assignment: StudentAssignment) => {
    setSelectedAssignment(assignment);
    setUnassignModalOpen(true);
  };

  /**
   * 배정 해제 실행
   */
  const handleConfirmUnassign = async () => {
    if (!workbookName || !selectedAssignment) return;

    try {
      await assignmentService.deleteAssignment(
        decodeURIComponent(workbookName),
        selectedAssignment.student_id
      );
      message.success('배정이 해제되었습니다');
      setUnassignModalOpen(false);
      setSelectedAssignment(null);
      setRefreshKey((prev) => prev + 1); // 새로고침
    } catch (error: any) {
      message.error('배정 해제 실패');
      console.error(error);
    }
  };

  /**
   * 학년 표시
   */
  const getGradeLabel = (grade: number): string => {
    if (grade >= 1 && grade <= 6) return `초${grade}`;
    if (grade >= 7 && grade <= 9) return `중${grade - 6}`;
    if (grade >= 10 && grade <= 12) return `고${grade - 9}`;
    return `${grade}`;
  };

  /**
   * 배정 현황 테이블 컬럼
   */
  const assignmentColumns: ColumnsType<StudentAssignment> = [
    {
      title: '학생 이름',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 150,
    },
    {
      title: '학생 이메일',
      dataIndex: 'student_email',
      key: 'student_email',
      width: 200,
    },
    {
      title: '제출 기한',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 150,
      render: (date) => (date ? new Date(date).toLocaleDateString('ko-KR') : '-'),
    },
    {
      title: '상태',
      dataIndex: 'is_completed',
      key: 'is_completed',
      width: 100,
      render: (isCompleted) => (
        <Tag color={isCompleted ? 'green' : 'orange'}>
          {isCompleted ? '완료' : '진행 중'}
        </Tag>
      ),
      filters: [
        { text: '완료', value: true },
        { text: '진행 중', value: false },
      ],
      onFilter: (value, record) => record.is_completed === value,
    },
    {
      title: '배정일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => new Date(date).toLocaleDateString('ko-KR'),
    },
    {
      title: '완료일',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 150,
      render: (date) => (date ? new Date(date).toLocaleDateString('ko-KR') : '-'),
    },
    {
      title: '액션',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleUnassign(record)}
        >
          배정 해제
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!workbook) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>문제집을 찾을 수 없습니다.</Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* 페이지 헤더 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              목록으로
            </Button>
            <div>
              <h2 style={{ margin: 0 }}>{workbook.name}</h2>
              <div style={{ marginTop: 8, color: '#666' }}>
                {workbook.year}년 · {getGradeLabel(workbook.grade)}
                {workbook.semester && ` · ${workbook.semester}학기`}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => setPdfModalOpen(true)}
              disabled={problems.length === 0}
            >
              답안지 생성
            </Button>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleOpenAssignModal}
            >
              학생 배정
            </Button>
          </div>
        </div>
      </Card>

      {/* 탭 */}
      <Card>
        <Tabs defaultActiveKey="problems">
          {/* 문제 목록 탭 */}
          <TabPane tab="문제 목록" key="problems">
            <ProblemList workbookName={workbook.name} />
          </TabPane>

          {/* 배정 현황 탭 */}
          <TabPane tab={`배정 현황 (${assignments?.length || 0})`} key="assignments">
            <Table
              columns={assignmentColumns}
              dataSource={assignments}
              rowKey="student_id"
              pagination={{
                pageSize: 20,
                showTotal: (total) => `총 ${total}명`,
              }}
              scroll={{ x: 900 }}
            />
          </TabPane>

          {/* 통계 탭 */}
          <TabPane tab="통계" key="stats">
            <AssignmentStats workbookName={workbook.name} />
          </TabPane>
        </Tabs>
      </Card>

      {/* 배정 모달 */}
      <AssignWorkbookModal
        open={assignModalOpen}
        workbook={workbook}
        onClose={() => setAssignModalOpen(false)}
        onSuccess={handleAssignSuccess}
      />

      {/* 배정 해제 확인 모달 */}
      <Modal
        title="배정 해제"
        open={unassignModalOpen}
        onOk={handleConfirmUnassign}
        onCancel={() => {
          setUnassignModalOpen(false);
          setSelectedAssignment(null);
        }}
        okText="해제"
        okType="danger"
        cancelText="취소"
      >
        {selectedAssignment && (
          <p>
            "<strong>{selectedAssignment.student_name}</strong>" 학생의 "
            <strong>{workbook?.name}</strong>" 문제집 배정을 해제하시겠습니까?
          </p>
        )}
      </Modal>

      {/* PDF 답안지 생성 모달 */}
      <AnswerSheetGenerator
        open={pdfModalOpen}
        workbookName={workbook?.name || ''}
        problems={problems}
        onClose={() => setPdfModalOpen(false)}
      />
    </div>
  );
};
